import { useCallback, useEffect, useState } from 'react';

import { TaskResponse } from '../client';
import { RandomNumberGenerator } from '../utils';
import { useTaskCompletedEffect } from './useTaskCompletedEffect';

type Operator = '+' | '-' | '*';
const OPERATORS: Operator[] = ['+', '-', '*'];

interface EquationMiniGame {
  equation: string;
  provideAnswer: (answer: string) => void;
  resetError: () => void;
  gameWon: boolean;
  errorMessage: string;
}

interface EquationData {
  equation: string;
  answer: number;
}

export function useEquationMiniGame(
  task: TaskResponse,
  onTaskStarted: (task: TaskResponse) => Promise<void>,
  onTaskProgress: (task: TaskResponse, progress: number) => void,
  onTaskCompleted: (task: TaskResponse) => Promise<void>,
): EquationMiniGame {
  const gameWon = useTaskCompletedEffect(task, onTaskCompleted);
  const { equation, answer } = useEquationGenerator(task.total_effort, task.id);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    // indicate task is started as soon as component loads
    onTaskStarted(task);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const provideAnswer = useCallback(
    (userAnswer: string) => {
      if (parseFloat(userAnswer) === answer) {
        onTaskProgress(task, task.total_effort);
        setErrorMessage('');
      } else {
        setErrorMessage('Incorrect answer. Try again!');
      }
    },
    [answer, task, onTaskProgress],
  );

  const resetError = useCallback(() => {
    setErrorMessage('');
  }, []);

  return {
    equation,
    provideAnswer,
    resetError,
    gameWon,
    errorMessage,
  };
}

export function useEquationGenerator(complexity: number, seed: string): EquationData {
  const [equation, setEquation] = useState<string>('');
  const [answer, setAnswer] = useState<number>(0.1);

  useEffect(() => {
    const { equation, answer } = generateEquation(complexity, seed);
    setEquation(equation);
    setAnswer(answer);
  }, [complexity, seed]);

  return { equation, answer };
}

const generateEquation = (
  complexity: number,
  seed: string,
  seedOffset = 0,
  minTerms = 2,
  maxTerms = 6,
  minOperandRange = 10,
  maxOperandRange = 50,
  minMultiplicationLimit = 20,
  maxMultiplicationLimit = 100,
): EquationData => {
  const numTerms = interpolate(complexity, minTerms, maxTerms);
  const operandRange = interpolate(complexity, minOperandRange, maxOperandRange);
  const multiplicationLimit = interpolate(complexity, minMultiplicationLimit, maxMultiplicationLimit);
  const compositeSeed = `${seed}-${seedOffset}`;

  const rng = new RandomNumberGenerator(compositeSeed);
  let numbers = [rng.number(2, operandRange)];
  let operators: string[] = [];
  let prevOperator: string | null = null;

  while (numbers.length < numTerms) {
    const operator: string = prevOperator === '*' ? rng.element(OPERATORS, ['*']) : rng.element(OPERATORS);
    let nextNumber = rng.number(2, operandRange);

    if (operator === '*') {
      nextNumber = regenerateNextNumberIfMultiplicationIsTooComplex(
        numbers[numbers.length - 1],
        nextNumber,
        operandRange,
        multiplicationLimit,
        rng,
      );
    }

    numbers.push(nextNumber);
    operators.push(operator);
    prevOperator = operator;
  }

  let equationStr = numbers[0].toString();
  for (let i = 0; i < operators.length; i++) {
    equationStr += ` ${operators[i]} ${numbers[i + 1]}`;
  }

  let answer;
  try {
    // eslint-disable-next-line no-eval
    answer = eval(equationStr);
  } catch (error) {
    console.error(`Regenerating broken equation (${equationStr})`);
    return generateEquation(complexity, seed, seedOffset + 1);
  }

  if (typeof answer !== 'number' || !Number.isFinite(answer) || answer < 0) {
    console.log(`Regenerating equation (${equationStr}) to make it produce a valid non-negative integer result`);
    return generateEquation(complexity, seed, seedOffset + 1);
  }

  return { equation: equationStr, answer };
};

const interpolate = (complexity: number, minValue: number, maxValue: number): number => {
  return Math.round(minValue + ((maxValue - minValue) * (complexity - 1)) / 99);
};

const regenerateNextNumberIfMultiplicationIsTooComplex = (
  prevNumber: number,
  nextNumber: number,
  operandRange: number,
  multiplicationLimit: number,
  rng: RandomNumberGenerator,
): number => {
  const MAX_ATTEMPTS = 10;
  const FALLBACK_VALUE = 2;

  let remainingAttempts = MAX_ATTEMPTS;
  while (prevNumber * nextNumber > multiplicationLimit && remainingAttempts > 0) {
    nextNumber = rng.number(2, operandRange);
    remainingAttempts--;
  }

  return remainingAttempts > 0 ? nextNumber : FALLBACK_VALUE;
};
